/* eslint-disable camelcase */
import { Permission, SUPERUSER } from '../types/enums';
import { EventEmitter, SubscriberFunction } from '../utils/event-emitter';
import { getQueryString } from '../utils/query';
import { callApi } from './api';
import { authState } from './auth-state';
import { UserAuthType } from './users';

export type CurrentUser = {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  slackId: string;
  permissions: { name: Permission | typeof SUPERUSER; params: any }[];
  hasPassword: boolean;
  isAdz: boolean;
};

export type SsoProvider = 'google' | 'facebook';

type SsoLoginUrl = {
  url: string;
};

type PostLoginRedirectResponse = {
  redirectUrl: string;
};

export type LoginResponse = {
  mfaRequired: null | boolean;
  mfaLink: null | string;
  loginCompleted?: boolean;
};

export type UserSetupDetails = {
  email: string;
  authType: UserAuthType | null;
  authRequestParams: Record<string, string> | null;
  loginCompleted: boolean;
  mfaRequired: boolean;
  mfaLink: string | null;
};

export type UserSetupRequest = {
  token: string;
  authType: 'password' | 'sso';
  password?: string;
  firstName?: string;
  lastName?: string;
};

export class AuthService {
  private user: CurrentUser = {
    id: 0,
    firstName: '',
    lastName: '',
    email: '',
    slackId: '',
    permissions: [],
    hasPassword: false,
    isAdz: false
  };

  private readonly eventEmitter = new EventEmitter();

  async getSsoAuthUrl(
    provider: SsoProvider,
    serviceRedirectUrl?: string,
    clientId?: string,
    authRequestParams?: Record<string, string>
  ): Promise<SsoLoginUrl> {
    const query = getQueryString({
      service_redirect_url: serviceRedirectUrl,
      client_id: clientId,
      auth_request_params: authRequestParams ? JSON.stringify(authRequestParams) : undefined
    });

    return callApi({ url: `/auth/${provider}_login_auth_url/?${query}` });
  }

  async loginWithGoogleIdToken(
    credential: string,
    serviceRedirectUrl?: string,
    clientId?: string,
    authRequestParams?: Record<string, string>
  ): Promise<void> {
    await callApi({
      url: '/auth/google_login_id_token/',
      method: 'POST',
      data: {
        credential,
        service_redirect_url: serviceRedirectUrl,
        client_id: clientId,
        auth_request_params: authRequestParams
      }
    });
    authState.setLoggedIn(true);
  }

  async loginWithSsoCode(provider: SsoProvider, code: string, state?: string): Promise<void> {
    const response = await callApi({
      url: `/auth/${provider}_login_auth_code/`,
      method: 'POST',
      data: { code, state }
    });
    authState.setLoggedIn(true);
    return response;
  }

  async generateOneTimeCode(clientId: string): Promise<string> {
    return callApi({ url: '/auth/generate_one_time_code/', method: 'POST', data: { client_id: clientId } });
  }

  async getPostLoginRedirect(serviceRedirectUrl?: string, clientId?: string): Promise<PostLoginRedirectResponse> {
    return callApi({
      url: '/auth/post_login_redirect/',
      method: 'POST',
      data: {
        service_redirect_url: serviceRedirectUrl,
        client_id: clientId
      }
    });
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await callApi({
      url: '/auth/login/',
      method: 'POST',
      data: { email, password }
    });
    if (response.loginCompleted) {
      authState.setLoggedIn(true);
    }

    return response;
  }

  async verifyMfa(code: string): Promise<{ loginCompleted: boolean; passwordExpired: boolean }> {
    const response = await callApi({ url: '/auth/verify_mfa_code/', method: 'POST', data: { code } });
    if (response.loginCompleted) {
      authState.setLoggedIn(true);
    }

    return response;
  }

  async logout(): Promise<void> {
    await callApi({ url: '/auth/login/', method: 'DELETE' });
    authState.setLoggedIn(false);
  }

  async loadLoggedInState(): Promise<void> {
    try {
      await callApi({ url: '/auth/login/' });
      authState.setLoggedIn(true);
    } catch {
      authState.setLoggedIn(false);
    }
  }

  async loadUserInfo(): Promise<void> {
    this.user = await callApi({ url: '/users/me/?platform=iam' });
    this.eventEmitter.dispatchEvent();
  }

  hasPermission(permission?: Permission | ''): boolean {
    // Empty string for admin only routes
    return (
      permission === undefined ||
      !!this.user.permissions.find((perm) => perm.name === SUPERUSER) ||
      !!this.user.permissions.find((perm) => perm.name === Permission)
    );
  }

  getUserInfo(): CurrentUser {
    return this.user;
  }

  registerSubscriber(subscriber: SubscriberFunction): void {
    this.eventEmitter.registerSubscriber(subscriber);
  }

  unregisterSubscriber(subscriber: SubscriberFunction): void {
    this.eventEmitter.unregisterSubscriber(subscriber);
  }

  async changeUserPassword({ oldPassword, newPassword }: { oldPassword: string; newPassword: string }): Promise<void> {
    await callApi({
      url: '/auth/change_password/',
      method: 'POST',
      data: {
        oldPassword,
        newPassword
      }
    });
  }

  async changeExpiredPassword({
    oldPassword,
    newPassword
  }: {
    oldPassword: string;
    newPassword: string;
  }): Promise<void> {
    await callApi({
      url: '/auth/change_expired_password/',
      method: 'POST',
      data: {
        oldPassword,
        newPassword
      }
    });
    authState.setLoggedIn(true);
  }

  async requestPasswordReset({ email }: { email: string }): Promise<void> {
    await callApi({ url: '/auth/forgot_password/', method: 'POST', data: { email } });
  }

  async resetPassword({ password, token }: { password: string; token: string }): Promise<void> {
    await callApi({ url: '/auth/reset_password/', method: 'POST', data: { newPassword: password, token } });
  }

  async getUserSetupDetails(token: string): Promise<UserSetupDetails> {
    return callApi({ url: `/auth/get_user_setup_details/?token=${token}` });
  }

  async validatePasswordResetToken({ token }: { token: string }): Promise<void> {
    return callApi({ url: `/auth/reset_password/token_validate/?token=${token}` });
  }

  async setupUser(data: UserSetupRequest): Promise<UserSetupDetails> {
    const response = await callApi({
      url: '/auth/setup_user/',
      method: 'POST',
      data
    });
    if (response.loginCompleted) {
      authState.setLoggedIn(true);
    }

    return response;
  }

  async registerWithEmail(email: string, authRequestParams?: Record<string, string>): Promise<void> {
    return callApi({
      url: '/auth/register/',
      method: 'POST',
      data: { email, authRequestParams }
    });
  }

  async resendVerificationEmail(email: string): Promise<void> {
    return callApi({
      url: '/auth/users/resend_setup_mail/',
      method: 'POST',
      data: { email }
    });
  }
}

export const authService = new AuthService();
